import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, CalendarRange } from "lucide-react";

export const RANGE_OPTIONS = [
  { id: "daily", label: "Daily", hint: "Last 24 hours" },
  { id: "weekly", label: "Weekly", hint: "Last 7 days" },
  { id: "monthly", label: "Monthly", hint: "Last 30 days" },
  { id: "quarterly", label: "Quarterly", hint: "Last 90 days" },
  { id: "semi_annual", label: "Semi-Annual", hint: "Last 180 days" },
  { id: "annual", label: "Annual", hint: "Last 365 days" },
  { id: "all_time", label: "All Time", hint: "Everything" },
];

export const DEFAULT_RANGE = "monthly";

export function rangeLabel(id) {
  return RANGE_OPTIONS.find((r) => r.id === id)?.label || "All Time";
}

/**
 * Pill-style range picker with dropdown. Compact (fits in a header action row).
 *
 * <RangeSelect value={range} onChange={setRange} testIdPrefix="dashboard-range" />
 */
export default function RangeSelect({ value, onChange, testIdPrefix = "range" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = RANGE_OPTIONS.find((r) => r.id === value) || RANGE_OPTIONS[2];

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-3.5 py-2 rounded-full text-sm bg-white/[0.06] hover:bg-white/[0.10] text-white border border-white/[0.08] inline-flex items-center gap-2 transition min-w-[140px] justify-between"
        data-testid={`${testIdPrefix}-trigger`}
      >
        <span className="flex items-center gap-2">
          <CalendarRange className="w-3.5 h-3.5 text-white/50" />
          <span>{current.label}</span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-56 rounded-2xl border border-white/[0.08] bg-[#0a0a0a]/96 backdrop-blur-2xl shadow-2xl shadow-black/60 p-1.5 z-50 animate-in fade-in duration-100"
          data-testid={`${testIdPrefix}-menu`}
        >
          {RANGE_OPTIONS.map((opt) => {
            const isActive = opt.id === value;
            return (
              <button
                key={opt.id}
                onClick={() => { onChange(opt.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 rounded-xl flex items-center justify-between gap-3 transition ${
                  isActive ? "bg-[#007AFF]/15 text-white" : "hover:bg-white/[0.06] text-white/80"
                }`}
                data-testid={`${testIdPrefix}-opt-${opt.id}`}
              >
                <div>
                  <div className="text-sm">{opt.label}</div>
                  <div className="text-[10px] text-white/40">{opt.hint}</div>
                </div>
                {isActive && <Check className="w-3.5 h-3.5 text-[#007AFF]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
